// src/pages/shop/MessagesPage.js - UPDATED with swipe gestures and chat selection tracking

import React, { useState, useEffect, useReducer, useMemo, useCallback, useRef } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
// ADD to existing imports (after existing lucide-react imports)
import { Zap, ZapOff } from 'lucide-react';
import { doc as firestoreDoc, getDoc as firestoreGetDoc, updateDoc as firestoreUpdateDoc } from 'firebase/firestore';

import {
  MessageCircle, AlertCircle, X, Clock, Check, Download, ShoppingCart, Filter, ChevronDown, Store,
  CheckSquare, Trash2
} from 'lucide-react';
import { signInAnonymously } from 'firebase/auth';
import { 
  collection, query, where, orderBy, onSnapshot, doc, updateDoc, getDoc
} from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

// Import components
import MessagesList from './components/MessagesList';
import ChatView from './components/ChatView';
import EmptyState from './components/EmptyState';
import { RefreshCw, Pin } from 'lucide-react';
import { WELCOME_STYLES } from '../../theme/welcomeStyles';
import { useActiveShop } from '../../contexts/ActiveShopContext';

// UPDATED: New filter options for Buying/Selling
const MODE_OPTIONS = [
  { key: 'selling', label: 'Selling' },
  { key: 'buying', label: 'Buying' }
  
];

const STATUS_FILTER_OPTIONS = [
  { key: 'all', label: 'All', icon: MessageCircle },
  { key: 'active', label: 'Active', icon: Clock },
  { key: 'completed', label: 'Completed', icon: Check },
  { key: 'cancelled', label: 'Cancelled', icon: X }
];

// Chat state reducer - UPDATED to include mode
const chatReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_CHATS':
      return { ...state, chats: action.payload, loading: false };
    case 'SET_SELECTED_CHAT':
      return { ...state, selectedChat: action.payload };
    case 'SET_SEARCH_TERM':
      return { ...state, searchTerm: action.payload };
    case 'SET_FILTER':
      return { ...state, activeFilter: action.payload };
    case 'SET_MODE':
      return { ...state, activeMode: action.payload };
    case 'SET_MOBILE_VIEW':
      return { ...state, showChatList: action.payload };
    case 'SET_IS_GUEST': // ✅ NEW
      return { ...state, isGuest: action.payload };
    default:
      return state;
  }
};

// Initial state - UPDATED
const initialChatState = {
  chats: [],
  selectedChat: null,
  searchTerm: '',
  activeFilter: 'all',
  activeMode: 'selling', // ✅ CHANGED: Default to 'selling' for guests
  loading: true,
  showChatList: true,
  isGuest: false // ✅ NEW: Track if user is guest
};


const AutoAcceptToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 1rem;
  background: ${props => props.enabled
    ? 'rgba(76, 175, 80, 0.15)'
    : 'rgba(0, 0, 0, 0.3)'};
  border: 1px solid ${props => props.enabled
    ? 'rgba(76, 175, 80, 0.5)'
    : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 10px;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;

  &:hover {
    background: ${props => props.enabled
      ? 'rgba(76, 175, 80, 0.2)'
      : 'rgba(255, 255, 255, 0.05)'};
    border-color: ${props => props.enabled
      ? 'rgba(76, 175, 80, 0.7)'
      : 'rgba(255, 255, 255, 0.2)'};
  }

  .toggle-icon {
    color: ${props => props.enabled ? '#4CAF50' : 'rgba(255,255,255,0.4)'};
    display: flex;
    align-items: center;
    transition: color 0.3s;
  }

  .toggle-label {
    flex: 1;
    .title {
      font-size: 0.85rem;
      font-weight: 600;
      color: ${props => props.enabled ? '#4CAF50' : 'rgba(255,255,255,0.7)'};
      transition: color 0.3s;
    }
    .subtitle {
      font-size: 0.7rem;
      color: rgba(255,255,255,0.4);
      margin-top: 0.1rem;
    }
  }

  .toggle-switch {
    width: 36px;
    height: 20px;
    background: ${props => props.enabled
      ? 'linear-gradient(135deg, #4CAF50, #388E3C)'
      : 'rgba(255,255,255,0.15)'};
    border-radius: 10px;
    position: relative;
    transition: background 0.3s;
    flex-shrink: 0;

    &::after {
      content: '';
      position: absolute;
      top: 3px;
      left: ${props => props.enabled ? '18px' : '3px'};
      width: 14px;
      height: 14px;
      background: white;
      border-radius: 50%;
      transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 1px 4px rgba(0,0,0,0.3);
    }
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    .toggle-label .title { font-size: 0.8rem; }
    .toggle-label .subtitle { font-size: 0.65rem; }
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .left-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .logo {
      width: 40px;
      height: 40px;
      object-fit: contain;
      cursor: pointer;
      transition: transform 0.3s ease;
      
      &:hover {
        transform: scale(1.05);
      }
    }
    
    .shop-name {
      font-size: 1.5rem;
      background: ${props => props.theme?.colors?.accentGradient || 'linear-gradient(45deg, #800000, #4A0404)'};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      font-family: ${props => props.theme?.fonts?.heading || 'inherit'};
      font-weight: 700;
      letter-spacing: -0.02em;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        background: ${props => props.theme?.colors?.accentGradient || 'linear-gradient(45deg, #A00000, #5A0404)'};
        -webkit-background-clip: text;
        text-decoration: underline;
        text-decoration-thickness: 2px;
        text-underline-offset: 4px;
      }
    }
    
    .guest-badge {
      background: rgba(255, 193, 7, 0.2);
      color: #FFC107;
      padding: 0.25rem 0.75rem;
      border-radius: 12px;
      font-size: 0.8rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  @media (max-width: 768px) {
    .left-header {
      gap: 0.75rem;
      
      .logo {
        width: 32px;
        height: 32px;
      }
      
      .shop-name {
        font-size: 1.2rem;
      }
      
      .guest-badge {
        font-size: 0.7rem;
        padding: 0.2rem 0.6rem;
      }
    }
  }
`;

// Styled components
const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme?.colors?.background || 'linear-gradient(to bottom, #0B0B3B, #1A1A4C)'};
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding-bottom: 80px;
    min-height: calc(100vh - 80px);
  }
`;

const PageHeader = styled.div`
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.6)'}CC`};
  backdrop-filter: blur(10px);
  padding: 1.5rem 2rem;
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent || 'rgba(128, 0, 0, 0.3)'}30`};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MainContent = styled.div`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0;
    top: 0;
    bottom: 80px;
  }
  
  @media (max-width: 480px) {
    top: 0;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  height: 100%;
  position: relative;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const MobileBackButton = styled.button`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: transparent;
    border: none;
    color: #FFFFFF;
    cursor: pointer;
    padding: 1rem;
    margin-bottom: 1rem;
    font-size: 0.95rem;
    font-family: ${props => props.theme?.fonts?.body || 'inherit'};
    font-weight: 500;
    border-bottom: 1px solid rgba(128, 0, 0, 0.2);
    width: 100%;
    justify-content: flex-start;
    
    &:hover {
      background: rgba(128, 0, 0, 0.1);
    }
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

// NEW: Mode tabs container with swipe support
const ModeTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0 0.5rem;
  position: relative;
  touch-action: pan-y; /* Allow vertical scroll but enable horizontal swipe detection */
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    margin-bottom: 0.5rem; /* ✅ Reduced spacing on mobile */
  }
`;

// NEW: Mode tab button
const ModeTab = styled.button`
  flex: 1;
  background: ${props => props.active ? 'rgba(128, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
  border: 1px solid ${props => props.active ? '#800000' : 'rgba(128, 0, 0, 0.3)'};
  color: ${props => props.active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: ${props => props.theme?.fonts?.body || 'inherit'};
  letter-spacing: 0.02em;

  &:hover {
    background: rgba(128, 0, 0, 0.3);
    border-color: #800000;
    color: #FFFFFF;
  }

  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
  }
`;

// NEW: Filter dropdown menu - positioned relative to search bar
const FilterDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0.5rem;
  background: ${props => props.theme?.colors?.background || '#111'};
  border: 1px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 8px;
  padding: 0.5rem;
  min-width: 150px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  z-index: 100;
  display: ${props => props.show ? 'block' : 'none'};
  
  @media (max-width: 768px) {
    right: 0.5rem;
  }
`;

const FilterOption = styled.button`
  width: 100%;
  background: ${props => props.active ? 'rgba(128, 0, 0, 0.3)' : 'transparent'};
  border: none;
  color: ${props => props.active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'};
  padding: 0.75rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: ${props => props.theme?.fonts?.body || 'inherit'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
  
  &:hover {
    background: rgba(128, 0, 0, 0.2);
    color: #FFFFFF;
  }

  svg {
    width: 14px;
    height: 14px;
  }
  
  .count {
    background: ${props => props.active ? '#800000' : 'rgba(255, 255, 255, 0.2)'};
    color: white;
    padding: 0.1rem 0.4rem;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 700;
    min-width: 16px;
    text-align: center;
    margin-left: auto;
  }
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.25rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: rgba(255,255,255,0.8);

  .count-label { flex: 1; font-weight: 600; }

  button {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.35rem 0.75rem;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 600;
    transition: all 0.2s;
  }

  .delete-sel {
    background: rgba(231, 76, 60, 0.2);
    color: #e74c3c;
    border: 1px solid rgba(231, 76, 60, 0.4);
    &:hover:not(:disabled) { background: rgba(231, 76, 60, 0.35); }
    &:disabled { opacity: 0.4; cursor: default; }
  }

  .cancel-sel {
    background: rgba(255,255,255,0.07);
    color: rgba(255,255,255,0.7);
    border: 1px solid rgba(255,255,255,0.15);
    &:hover { background: rgba(255,255,255,0.12); color: #fff; }
  }
`;

const SearchInput = styled.div`
  position: relative;
  margin-bottom: 1rem;
  font-family: ${props => props.theme?.fonts?.body || 'inherit'};
  
  @media (max-width: 768px) {
    margin-bottom: 0.5rem; /* ✅ Reduced from 1rem to 0.5rem */
  }
  
  input {
    width: 100%; /* ✅ Changed from 85% to 100% */
    padding: 0.75rem 3rem 0.75rem 1rem; /* ✅ Right padding for filter button */
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(128, 0, 0, 0.3);
    border-radius: 12px;
    color: #FFFFFF;
    font-size: 0.9rem;
    font-family: ${props => props.theme?.fonts?.body || 'inherit'};
    font-weight: 400;
    line-height: 1.4;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #800000;
      box-shadow: 0 0 0 2px rgba(128, 0, 0, 0.3);
      background: rgba(0, 0, 0, 0.6);
    }
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.6);
      font-style: normal;
    }

    @media (max-width: 768px) {
      padding: 0.75rem 3rem 0.75rem 1rem;
    }
  }

  .clear-button {
    position: absolute;
    right: 2.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.7;
    z-index: 2; /* ✅ Ensure it's above filter button */
    
    &:hover {
      opacity: 1;
    }
  }
  
  /* ✅ NEW: Filter button inside search bar */
  .filter-button-inline {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: ${props => props.filterActive ? 'rgba(128, 0, 0, 0.3)' : 'transparent'};
    border: 1px solid ${props => props.filterActive ? '#800000' : 'rgba(128, 0, 0, 0.3)'};
    color: ${props => props.filterActive ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'};
    padding: 0.5rem;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;
    z-index: 1;
    
    &:hover {
      background: rgba(128, 0, 0, 0.3);
      border-color: #800000;
      color: #FFFFFF;
    }
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const ThemeControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RefreshButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme?.colors?.text || "white"};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1) rotate(90deg);
    color: ${props => props.theme?.colors?.accent || "#800000"};
  }
  
  &.spinning {
    animation: spin 0.5s ease-in-out;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const PinButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.isPinned ? (props.theme?.colors?.accent || '#800000') : 'white'};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const MessagesPage = () => {
  const { activeShopId, isOwnShop, accessibleShops, setActiveShop } = useActiveShop();
  const [shopSelectorOpen, setShopSelectorOpen] = useState(false);
  const [chatState, chatDispatch] = useReducer(chatReducer, initialChatState);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(null);
  const [isPinned, setIsPinned] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(WELCOME_STYLES.STYLE_1);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [shopName, setShopName] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [currentGuestSession, setCurrentGuestSession] = useState(null);
  const targetChatId = searchParams.get('chat');
  const scannedCode = searchParams.get('code');

  // ✅ NEW: Skeleton chat shown instantly on order click, before the real
  // Firestore doc exists — see TransactionService.buildOptimisticChat.
  const [optimisticChat, setOptimisticChat] = useState(location.state?.optimisticChat || null);

  useEffect(() => {
    if (location.state?.optimisticChat) {
      setOptimisticChat(location.state.optimisticChat);
      // Jump straight to the chat body skeleton on mobile — without this the
      // list/body panes default to showing the list first (showChatList starts
      // true) and only flip once the real chat doc loads and matches targetChatId,
      // so the redirect from placing an order briefly showed the message list
      // instead of the unified chat/order skeleton.
      chatDispatch({ type: 'SET_MOBILE_VIEW', payload: false });
    }
  }, [location.state]);

  // Once the real chat has loaded and is selected, drop the skeleton
  useEffect(() => {
    if (chatState.selectedChat?.id === targetChatId) {
      setOptimisticChat(null);
    }
  }, [chatState.selectedChat?.id, targetChatId]);

  // location.state?.optimisticChat updates synchronously with navigation (React
  // Router flushes it before the next render), unlike the optimisticChat state
  // which is set via useEffect and lags by one render. Using location.state as an
  // immediate fallback prevents a single-frame gap where displayChat would be null
  // during the optimistic-temp-id → real-id transition, which caused the messages
  // backdrop to flash briefly.
  const locationOptimistic = location.state?.optimisticChat;
  const displayChat = chatState.selectedChat?.id === targetChatId
    ? chatState.selectedChat
    : (optimisticChat?.id === targetChatId
        ? optimisticChat
        : (locationOptimistic?.id === targetChatId ? locationOptimistic : null));
  
  // ✅ NEW: Touch gesture state for swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const chatListRef = useRef(null);
  const isGuestUser = auth.currentUser?.type === 'guest';
  
  // ✅ NEW: Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const [autoAcceptEnabled, setAutoAcceptEnabled] = useState(false);
  const [autoAcceptLoading, setAutoAcceptLoading] = useState(false);

  // Load auto-accept setting on mount
  useEffect(() => {
    const loadSellerSettings = async () => {
      if (!auth.currentUser || auth.currentUser.isAnonymous) return;
      try {
        const shopRef = firestoreDoc(db, 'shops', auth.currentUser.uid);
        const shopSnap = await firestoreGetDoc(shopRef);
        if (shopSnap.exists()) {
          const data = shopSnap.data();
          setAutoAcceptEnabled(data.autoAcceptOrders !== false);
        }
      } catch (err) {
        console.error('Error loading seller settings:', err);
      }
    };
    loadSellerSettings();
  }, [auth.currentUser?.uid]);

  const handleToggleAutoAccept = async () => {
    if (!auth.currentUser || auth.currentUser.isAnonymous) return;
    setAutoAcceptLoading(true);
    try {
      const newValue = !autoAcceptEnabled;
      const shopRef = firestoreDoc(db, 'shops', auth.currentUser.uid);
      await firestoreUpdateDoc(shopRef, {
        autoAcceptOrders: newValue,
        autoAcceptUpdatedAt: new Date().toISOString()
      });
      setAutoAcceptEnabled(newValue);
      console.log('✅ Auto-accept toggled:', newValue);
    } catch (err) {
      console.error('Error toggling auto-accept:', err);
      alert('Failed to update setting. Please try again.');
    } finally {
      setAutoAcceptLoading(false);
    }
  };

  const getCurrentUserContext = () => {
    const guestSession = localStorage.getItem('guest_session');
    if (guestSession) {
      try {
        const session = JSON.parse(guestSession);
        return {
          type: 'guest',
          id: session.guestId,
          name: session.displayName,
          session: session
        };
      } catch (error) {
        console.error('Error parsing guest session:', error);
      }
    }
    
    if (auth.currentUser) {
      return {
        type: 'authenticated',
        id: auth.currentUser.uid,
        name: auth.currentUser.displayName || auth.currentUser.email
      };
    }
    
    return null;
  };

  

  // Around line 150-160 in MessagesPage.js - UPDATED guest detection
useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const isGuestParam = urlParams.get('guest') === 'true';
  const guestSession = localStorage.getItem('guest_session');
  
  // ✅ IMPROVED: Check multiple sources for guest status
  const hasGuestSession = !!guestSession;
  const isAnonymous = auth.currentUser?.isAnonymous === true;
  
  const isGuest = isGuestParam || hasGuestSession || isAnonymous;
  
  if (isGuest) {
    console.log('🔒 Guest user detected - locking to buying mode');
    
    // ✅ Set isGuest state
    if (!chatState.isGuest) {
      chatDispatch({ type: 'SET_IS_GUEST', payload: true });
    }
    
    // ✅ Force buying mode
    if (chatState.activeMode !== 'buying') {
      chatDispatch({ type: 'SET_MODE', payload: 'buying' });
    }
  } else {
    // ✅ Clear guest status for authenticated users
    if (chatState.isGuest) {
      chatDispatch({ type: 'SET_IS_GUEST', payload: false });
    }
  }
}, [chatState.isGuest, chatState.activeMode]);

  // ✅ NEW: Handle swipe gestures for tab switching
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      // Swipe left -> go to next tab (selling -> buying)
      if (chatState.activeMode === 'selling') {
        chatDispatch({ type: 'SET_MODE', payload: 'buying' });
      }
    }
    
    if (isRightSwipe) {
      // Swipe right -> go to previous tab (buying -> selling)
      if (chatState.activeMode === 'buying') {
        chatDispatch({ type: 'SET_MODE', payload: 'selling' });
      }
    }
  };

  const urlParams = new URLSearchParams(window.location.search);
  const isGuestParam = urlParams.get('guest') === 'true';


  useEffect(() => {
  const guestSession = localStorage.getItem('guest_session');
  const isAnonymous = auth.currentUser?.isAnonymous === true;

  // ✅ IMPROVED: Multiple checks for guest status
  if (chatState.isGuest || isGuestParam || guestSession || isAnonymous) {
    // Guests should ALWAYS be in buying mode
    if (chatState.activeMode !== 'buying') {
      console.log('🔒 Forcing guest to buying mode');
      chatDispatch({ type: 'SET_MODE', payload: 'buying' });
    }
  } else if (auth.currentUser && !auth.currentUser.isAnonymous) {
    // Authenticated users default to selling mode IF they have no chats
    if (chatState.activeMode === 'buying' && chatState.chats.length === 0) {
      console.log('📊 Setting authenticated user to selling mode');
      chatDispatch({ type: 'SET_MODE', payload: 'selling' });
    }
  }
}, [chatState.isGuest, isGuestParam, isGuestUser, auth.currentUser?.isAnonymous, chatState.activeMode, chatState.chats.length]);

  // NEW: Fetch shop name for authenticated users
  useEffect(() => {
    const fetchShopName = async () => {
      const userContext = getCurrentUserContext();
      
      if (userContext?.type === 'guest') {
        setShopName('Guest');
        return;
      }
      
      if (auth.currentUser) {
        try {
          const shopRef = doc(db, 'shops', auth.currentUser.uid);
          const shopSnap = await getDoc(shopRef);
          
          if (shopSnap.exists()) {
            setShopName(shopSnap.data().name || 'My Shop');
          } else {
            setShopName('My Shop');
          }
        } catch (error) {
          console.error('Error fetching shop name:', error);
          setShopName('My Shop');
        }
      }
    };
    
    fetchShopName();
  }, []);

  
  useEffect(() => {
    const pinnedStyleId = localStorage.getItem('pinnedStyleId');

    if (pinnedStyleId) {
      const pinnedStyle = Object.values(WELCOME_STYLES).find(
        style => style.id.toString() === pinnedStyleId
      );

      if (pinnedStyle) {
        setSelectedTheme(pinnedStyle);
        setIsPinned(true);
        return;
      }
    }

    const styles = Object.values(WELCOME_STYLES);
    const randomStyle = styles[Math.floor(Math.random() * styles.length)];
    setSelectedTheme(randomStyle);
  }, []);

  const refreshTheme = () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    
    const styles = Object.values(WELCOME_STYLES);
    const otherStyles = styles.filter(style => style.id !== selectedTheme.id);
    
    if (otherStyles.length > 0) {
      const randomStyle = otherStyles[Math.floor(Math.random() * otherStyles.length)];
      setSelectedTheme(randomStyle);

      if (isPinned) {
        localStorage.removeItem('pinnedStyleId');
        setIsPinned(false);
      }
    }

    setTimeout(() => setIsRefreshing(false), 500);
  };

  const togglePinStyle = () => {
    if (isPinned) {
      localStorage.removeItem('pinnedStyleId');
      setIsPinned(false);

      const styles = Object.values(WELCOME_STYLES).filter(
        style => style.id !== selectedTheme.id
      );

      if (styles.length > 0) {
        const randomStyle = styles[Math.floor(Math.random() * styles.length)];
        setSelectedTheme(randomStyle);
      }
    } else {
      localStorage.setItem('pinnedStyleId', selectedTheme.id.toString());
      setIsPinned(true);
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load chats (same as before)
  useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const isGuestUser = urlParams.get('guest') === 'true';
  
  const guestSession = localStorage.getItem('guest_session');
  const parsedGuestSession = guestSession ? JSON.parse(guestSession) : null;
  
  const userType = isGuestUser && parsedGuestSession ? 'guest' : 'authenticated';
  
  console.log('👤 User type determined:', {
    userType,
    isGuestUserParam: isGuestUser,
    hasGuestSession: !!parsedGuestSession,
    hasAuthUser: !!auth.currentUser,
    guestId: parsedGuestSession?.guestId
  });
  
  if (userType === 'guest' && parsedGuestSession) {
    const now = new Date();
    const expiresAt = new Date(parsedGuestSession.expiresAt);
    
    if (expiresAt <= now) {
      console.warn('⚠️ Guest session expired');
      localStorage.removeItem('guest_session');
      navigate('/auth', { replace: true });
      return;
    }
    
    console.log('✅ Valid guest session found:', parsedGuestSession.guestId);
    
    // ✅ IMPORTANT: Sign in anonymously if not already signed in
    const ensureAnonymousAuth = async () => {
      if (!auth.currentUser || !auth.currentUser.isAnonymous) {
        console.log('🔐 No anonymous session, creating one...');
        await signInAnonymously(auth);
        console.log('✅ Anonymous session created');
      }
    };
    
    ensureAnonymousAuth().catch(err => {
      console.error('❌ Failed to create anonymous session:', err);
    });

    chatDispatch({ type: 'SET_LOADING', payload: true });
    
    const fetchGuestChats = async () => {
      try {
        console.log('📡 Fetching guest chats via API...');
        
        const response = await fetch('/api/guest-operations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operation: 'get_chats', guestId: parsedGuestSession.guestId })
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch guest chats');
        }
        
        const data = await response.json();
        
        console.log(`✅ Loaded ${data.chats.length} guest chats via API`);
        
        // ✅ Deduplicate chats by transactionId
        const uniqueChats = [];
        const seenTransactionIds = new Set();
        
        for (const chat of data.chats) {
          const txId = chat.transactionId || chat.id;
          
          if (!seenTransactionIds.has(txId)) {
            seenTransactionIds.add(txId);
            uniqueChats.push({
              ...chat,
              isGuestOrder: true,
              isBuyer: true,
              isSeller: false,
              role: 'buyer',
              transactionId: txId,
              itemName: chat.itemName || chat.pendingPurchase?.itemName || 'Unknown Item',
              otherPartyName: chat.sellerName || 'Unknown Shop',
              createdAt: chat.createdAt ? new Date(chat.createdAt) : null,
              lastMessageTime: chat.lastMessageTime ? new Date(chat.lastMessageTime) : null,
              expiresAt: chat.expiresAt ? new Date(chat.expiresAt) : null
            });
          }
        }
        
        console.log(`📊 After deduplication: ${uniqueChats.length} unique chats`);
        
        chatDispatch({ type: 'SET_CHATS', payload: uniqueChats });
        
      } catch (error) {
        console.error('❌ Error fetching guest chats:', error);
        chatDispatch({ type: 'SET_LOADING', payload: false });
      }
    };
    
    fetchGuestChats();
    
    const pollInterval = setInterval(fetchGuestChats, 3000);
    
    return () => {
      clearInterval(pollInterval);
    };
  }
    
    if (!auth.currentUser) {
      console.log('🔐 No authenticated user found, redirecting to auth');
      navigate('/auth', { replace: true });
      return;
    }

    console.log('👤 Loading chats for authenticated user:', auth.currentUser.uid);
    // Clear stale chats from previous shop immediately on shop switch
    chatDispatch({ type: 'SET_CHATS', payload: [] });
    chatDispatch({ type: 'SET_LOADING', payload: true });

    const chatsRef = collection(db, 'chats');
    let regularChats = [];
    let guestOrders = [];

    const combineAndUpdate = () => {
      combineAll();
    };

    // When acting as a shared shop, also fetch that shop's seller chats
    // (the current user is not in the participants array for those chats)
    const sellingAsShopId = activeShopId || auth.currentUser.uid;
    const isActingAsSharedShop = sellingAsShopId !== auth.currentUser.uid;

    const regularChatsQuery = query(
      chatsRef,
      where('participants', 'array-contains', auth.currentUser.uid),
      orderBy('lastMessageTime', 'desc')
    );

    const unsubscribe1 = onSnapshot(regularChatsQuery, (snapshot) => {
  regularChats = snapshot.docs
    .flatMap(docSnap => {
      const chat = docSnap.data();
      const userId = auth.currentUser.uid;

      if (chat.hidden?.[userId]) return [];

      const baseChat = {
        id: docSnap.id,
        ...chat,
        roles: chat.roles || { [chat.buyerId]: 'buyer', [chat.sellerId]: 'seller' },
        transactionStatus: chat.pendingPurchase?.status,
        unreadCount: chat.unreadCount?.[userId] || 0,
      };

      // Self-order: emit two entries so it appears in both buying and selling tabs
      if (chat.isSelfOrder && chat.buyerId === userId && chat.sellerId === userId) {
        return [
          {
            ...baseChat,
            currentUserRole: 'buyer',
            selfOrderRole: 'buyer',
            otherPartyId: chat.sellerId,
            otherPartyName: chat.sellerName || 'Your Shop',
          },
          {
            ...baseChat,
            currentUserRole: 'seller',
            selfOrderRole: 'seller',
            otherPartyId: chat.buyerId,
            otherPartyName: chat.buyerName || 'You',
          },
        ];
      }

      let userRole = null;
      if (chat.roles && chat.roles[userId]) {
        userRole = chat.roles[userId];
      } else if (chat.buyerId === userId) {
        userRole = 'buyer';
      } else if (chat.sellerId === userId) {
        userRole = 'seller';
      } else {
        return []; // User not part of this chat
      }

      return [{
        ...baseChat,
        currentUserRole: userRole,
        otherPartyId: userRole === 'buyer' ? chat.sellerId : chat.buyerId,
        otherPartyName: userRole === 'buyer'
          ? (chat.sellerName || 'Unknown Seller')
          : (chat.buyerName || 'Unknown Buyer'),
      }];
    });
  
  combineAndUpdate();
});

    const guestOrdersQuery = query(
      chatsRef,
      where('sellerId', '==', sellingAsShopId),
      where('isGuestOrder', '==', true),
      orderBy('lastMessageTime', 'desc')
    );

    // Extra query for shared shop: fetch all non-guest selling chats by sellerId
    // (the shared user is not in participants, so regularChatsQuery won't catch them)
    let unsubscribe3 = null;
    let sharedShopChats = [];

    const combineAll = () => {
      // Deduplicate: self-order entries have selfOrderRole suffix to keep both
      const seen = new Set();
      const allChats = [...sharedShopChats, ...regularChats, ...guestOrders]
        .filter(chat => {
          const key = chat.id + (chat.selfOrderRole ? '_' + chat.selfOrderRole : '');
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .sort((a, b) => {
          const tA = a.lastMessageTime?.toMillis?.() ?? a.lastMessageTime?.getTime?.() ?? 0;
          const tB = b.lastMessageTime?.toMillis?.() ?? b.lastMessageTime?.getTime?.() ?? 0;
          return tB - tA;
        });
      chatDispatch({ type: 'SET_CHATS', payload: allChats });
    };

    if (isActingAsSharedShop) {
      // Auto-switch to selling mode so the user sees the shared shop's orders immediately
      if (chatState.activeMode !== 'selling') {
        chatDispatch({ type: 'SET_MODE', payload: 'selling' });
      }

      // Fetch ALL selling chats for this shop by sellerId — no isGuestOrder filter
      // because older docs may not have that field, and deduplication handles overlaps
      const sharedSellingQuery = query(
        chatsRef,
        where('sellerId', '==', sellingAsShopId),
        orderBy('lastMessageTime', 'desc')
      );
      unsubscribe3 = onSnapshot(sharedSellingQuery, (snapshot) => {
        sharedShopChats = snapshot.docs
          .map(docSnap => {
            const chat = docSnap.data();
            if (chat.hidden?.[sellingAsShopId]) return null;
            return {
              id: docSnap.id,
              ...chat,
              roles: chat.roles || { [chat.buyerId]: 'buyer', [chat.sellerId]: 'seller' },
              currentUserRole: 'seller',
              otherPartyId: chat.buyerId,
              otherPartyName: chat.buyerName || 'Unknown Buyer',
              // Use the shop's sellerId for unread count (acting as the shop)
              unreadCount: chat.unreadCount?.[sellingAsShopId] || 0,
              transactionStatus: chat.pendingPurchase?.status
            };
          })
          .filter(Boolean);
        combineAll();
      });
    }

    const unsubscribe2 = onSnapshot(guestOrdersQuery, (snapshot) => {
      guestOrders = snapshot.docs
        .map(docSnap => {
          const chat = docSnap.data();
          
          if (chat.hidden?.[auth.currentUser.uid]) {
            return null;
          }

          const expiresAt = chat.expiresAt?.toMillis ? chat.expiresAt.toMillis() : 0;
          if (Date.now() > expiresAt) {
            return null;
          }

          return {
            id: docSnap.id,
            ...chat,

            // ✅ KEEP ROLES MAP
            roles: chat.roles || {
              [chat.buyerId]: 'buyer',
              [chat.sellerId]: 'seller'
            },

            // ✅ CURRENT USER IS SELLER (since this is guestOrdersQuery)
            currentUserRole: 'seller',

            // Keep these for backwards compatibility
            otherPartyId: chat.buyerId,
            otherPartyName: chat.buyerName || 'Guest Customer',
            // Use sellingAsShopId for unread count — works for primary and shared shops
            unreadCount: chat.unreadCount?.[sellingAsShopId] || 0,
            transactionStatus: chat.pendingPurchase?.status
          };
        })
        .filter(Boolean);
      
      combineAndUpdate();
    });

    return () => {
      unsubscribe1();
      unsubscribe2();
      if (unsubscribe3) unsubscribe3();
    };
  }, [navigate, activeShopId]);

    // ✅ UPDATED: Better chat selection from URL with duplicate prevention
  useEffect(() => {
  const targetChatId = searchParams.get('chat');
  
  if (targetChatId && chatState.chats.length > 0) {
    const targetChat = chatState.chats.find(chat => chat.id === targetChatId);
    
    if (targetChat) {
      // ✅ Only select if different from current
      const shouldSelect = !chatState.selectedChat ||
                          chatState.selectedChat.id !== targetChatId;

      if (shouldSelect) {
        console.log('📌 Selecting chat from URL:', targetChatId);

        // ✅ FIX: For guests, ensure we have valid session before selecting
        if (targetChat.isGuestOrder && targetChat.isBuyer) {
          const guestSession = localStorage.getItem('guest_session');
          if (!guestSession) {
            console.error('❌ No guest session found');
            navigate('/messages', { replace: true });
            return;
          }

          try {
            const session = JSON.parse(guestSession);
            const guestIdMatches =
              targetChat.buyerId === session.guestId ||
              targetChat.guestId === session.guestId;

            if (!guestIdMatches) {
              console.error('❌ Guest ID mismatch');
              navigate('/messages', { replace: true });
              return;
            }
          } catch (error) {
            console.error('❌ Error validating guest session:', error);
            navigate('/messages', { replace: true });
            return;
          }
        }

        // ✅ Auto-switch to 'buying' tab when the target chat is a buyer chat
        const chatRole = targetChat.currentUserRole || targetChat.roles?.[auth.currentUser?.uid];
        if (chatRole === 'buyer' && chatState.activeMode !== 'buying') {
          chatDispatch({ type: 'SET_MODE', payload: 'buying' });
        }

        chatDispatch({ type: 'SET_SELECTED_CHAT', payload: targetChat });

        if (isMobile) {
          chatDispatch({ type: 'SET_MOBILE_VIEW', payload: false });
        }
      }
    } else if (!targetChatId.startsWith('pending-')) {
      console.warn('⚠️ Chat not found:', targetChatId);
    }
  }
}, [searchParams, chatState.chats, chatState.selectedChat?.id, isMobile, navigate]);

  
  // Handle logo click
  const handleLogoClick = () => {
    if (isGuestUser) {
      navigate('/');
    } else {
      navigate('/');
    }
  };
  
  // Handle shop name click
  const handleShopNameClick = () => {
    if (isGuestUser) {
      return;
    }
    
    if (shopName && shopName !== 'My Shop' && shopName !== 'Guest') {
      const formattedShopName = shopName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      
      window.open(`https://${formattedShopName}.kalkode.com`, '_blank');
    }
  };

  // UPDATED: Filter chats by mode AND status
  const getFilteredChats = useMemo(() => {
  let filtered = chatState.chats;

  const userIsGuest = chatState.isGuest || isGuestUser;
  
  // ✅ GET CURRENT USER ID
  let currentUserId = null;
  if (auth.currentUser) {
    currentUserId = auth.currentUser.uid;
  } else if (userIsGuest) {
    const guestSession = localStorage.getItem('guest_session');
    if (guestSession) {
      try {
        const session = JSON.parse(guestSession);
        currentUserId = session.guestId;
      } catch (error) {
        console.error('Error parsing guest session:', error);
      }
    }
  }
  
  if (!currentUserId) {
    console.warn('⚠️ No current user ID found');
    return [];
  }
  
  if (userIsGuest) {
    // ✅ For guests, filter by currentUserRole
    filtered = filtered.filter(chat => {
      const userRole = chat.currentUserRole || chat.roles?.[currentUserId];
      return userRole === 'buyer';
    });
  } else {
    // ✅ Apply mode filter for authenticated users using currentUserRole
    const sellingShopId = activeShopId || auth.currentUser?.uid;
    const actingAsNonPrimaryShop = sellingShopId && sellingShopId !== auth.currentUser?.uid;

    filtered = filtered.filter(chat => {
      const userRole = chat.currentUserRole || chat.roles?.[currentUserId];

      if (chatState.activeMode === 'buying') {
        // In buying mode: show only buyer chats, EXCLUDE guest orders
        return userRole === 'buyer' && !chat.isGuestOrder;
      } else if (chatState.activeMode === 'selling') {
        if (!userRole || userRole !== 'seller') return false;
        // When acting as a specific shop, only show that shop's selling chats
        if (actingAsNonPrimaryShop) {
          return chat.sellerId === sellingShopId;
        }
        return true;
      }
      return true;
    });
  }

  // Apply status filter
  if (chatState.activeFilter !== 'all') {
    filtered = filtered.filter(chat => {
      const status = chat.transactionStatus;
      
      switch (chatState.activeFilter) {
        case 'active':
          return ['pending_seller_acceptance', 'seller_accepted', 'paid', 'pending_send'].includes(status);
        case 'completed':
          return status === 'completed';
        case 'cancelled':
          return ['seller_rejected', 'withdrawn'].includes(status);
        default:
          return true;
      }
    });
  }

  // Apply search filter
  if (chatState.searchTerm) {
    filtered = filtered.filter(chat => 
      (chat.itemName?.toLowerCase().includes(chatState.searchTerm.toLowerCase())) ||
      (chat.otherPartyName?.toLowerCase().includes(chatState.searchTerm.toLowerCase())) ||
      (chat.lastMessage?.toLowerCase().includes(chatState.searchTerm.toLowerCase()))
    );
  }

  // ✅ Sort to show guest orders at the bottom (only in selling mode for authenticated users)
  if (!userIsGuest && chatState.activeMode === 'selling') {
    filtered = filtered.sort((a, b) => {
      // First priority: Guest orders go to bottom
      if (a.isGuestOrder && !b.isGuestOrder) return 1;
      if (!a.isGuestOrder && b.isGuestOrder) return -1;
      
      // Second priority: Sort by last message time (most recent first)
      const timeA = a.lastMessageTime?.toMillis ? a.lastMessageTime.toMillis() : 
                    a.lastMessageTime?.getTime ? a.lastMessageTime.getTime() : 0;
      const timeB = b.lastMessageTime?.toMillis ? b.lastMessageTime.toMillis() :
                    b.lastMessageTime?.getTime ? b.lastMessageTime.getTime() : 0;
      return timeB - timeA;
    });
  }

  return filtered;
}, [chatState.chats, chatState.activeMode, chatState.activeFilter, chatState.searchTerm, chatState.isGuest, isGuestUser, activeShopId]);

  const handleBackToChatList = useCallback(() => {
    if (isMobile) {
      chatDispatch({ type: 'SET_MOBILE_VIEW', payload: true });
      chatDispatch({ type: 'SET_SELECTED_CHAT', payload: null });
      setSearchParams({});
    }
  }, [isMobile, setSearchParams]);

  useEffect(() => {
    const handlePopState = () => {
      if (isMobile && chatState.selectedChat) {
        handleBackToChatList();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isMobile, chatState.selectedChat, handleBackToChatList]);

  // ✅ UPDATED: handleChatSelect to update URL and prevent duplicates
  const handleChatSelect = useCallback((chat) => {
    console.log('🎯 Chat selected:', chat.id);
    
    // Only dispatch if different from current
    if (!chatState.selectedChat || chatState.selectedChat.id !== chat.id) {
      chatDispatch({ type: 'SET_SELECTED_CHAT', payload: chat });
    }
    
    // ✅ Update URL
    setSearchParams({ chat: chat.id }, { replace: true });
    
    if (isMobile) {
      chatDispatch({ type: 'SET_MOBILE_VIEW', payload: false });
      window.history.pushState({}, '', `?chat=${chat.id}`);
    }
  }, [isMobile, setSearchParams, chatState.selectedChat?.id]);

  // UPDATED: Get filter counts by mode
  const getFilterCounts = useMemo(() => {
    const modeChats = chatState.chats.filter(chat => {
      if (chatState.activeMode === 'buying') {
        return chat.isBuyer;
      } else if (chatState.activeMode === 'selling') {
        return chat.isSeller;
      }
      return true;
    });

    const counts = {
      all: modeChats.length,
      active: 0,
      completed: 0,
      cancelled: 0
    };

    modeChats.forEach(chat => {
      const status = chat.transactionStatus;
      if (['pending_seller_acceptance', 'seller_accepted', 'paid'].includes(status)) {
        counts.active++;
      } else if (status === 'completed') {
        counts.completed++;
      } else if (['seller_rejected', 'withdrawn'].includes(status)) {
        counts.cancelled++;
      }
    });

    return counts;
  }, [chatState.chats, chatState.activeMode]);

  const handleDeleteChat = async (chatId, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const userContext = getCurrentUserContext();
    
    if (!userContext) {
      alert('You must be logged in to delete chats');
      return;
    }
    
    try {
      if (userContext.type === 'guest') {
        const response = await fetch('https://www.kalkode.com/api/guest-operations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            operation: 'delete_chat',
            chatId: chatId,
            guestId: userContext.id
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete chat');
        }
      } else {
        const chatRef = doc(db, 'chats', chatId);
        
        await updateDoc(chatRef, {
          [`hidden.${userContext.id}`]: true
        });
      }
      
      const updatedChats = chatState.chats.filter(chat => chat.id !== chatId);
      chatDispatch({ type: 'SET_CHATS', payload: updatedChats });
      
      if (chatState.selectedChat?.id === chatId) {
        chatDispatch({ type: 'SET_SELECTED_CHAT', payload: null });
        setSearchParams({}, { replace: true });
        
        if (isMobile) {
          chatDispatch({ type: 'SET_MOBILE_VIEW', payload: true });
        }
      }
      
      console.log('✅ Chat deleted successfully');
      
    } catch (error) {
      console.error('❌ Error deleting chat:', error);
      alert('Failed to delete conversation: ' + error.message);
    }
  };

  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };

  const toggleSelectChat = (chatId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(chatId) ? next.delete(chatId) : next.add(chatId);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} conversation${selectedIds.size > 1 ? 's' : ''}?`)) return;
    const userContext = getCurrentUserContext();
    if (!userContext) return;
    const idsToDelete = [...selectedIds];
    for (const chatId of idsToDelete) {
      try {
        if (userContext.type === 'guest') {
          await fetch('https://www.kalkode.com/api/guest-operations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ operation: 'delete_chat', chatId, guestId: userContext.id })
          });
        } else {
          await updateDoc(doc(db, 'chats', chatId), { [`hidden.${userContext.id}`]: true });
        }
      } catch (err) {
        console.error('Failed to delete', chatId, err);
      }
    }
    const updatedChats = chatState.chats.filter(c => !idsToDelete.includes(c.id));
    chatDispatch({ type: 'SET_CHATS', payload: updatedChats });
    if (selectedIds.has(chatState.selectedChat?.id)) {
      chatDispatch({ type: 'SET_SELECTED_CHAT', payload: null });
      setSearchParams({}, { replace: true });
      if (isMobile) chatDispatch({ type: 'SET_MOBILE_VIEW', payload: true });
    }
    exitSelectMode();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showFilterDropdown) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showFilterDropdown]);

  return (
    <ThemeProvider theme={selectedTheme}>
      <PageContainer>
        <PageHeader theme={currentTheme}>
          <HeaderContent theme={currentTheme}>
            <div className="left-header">
              <img 
                src="/logo512.png" 
                alt="KalKode" 
                className="logo"
                onClick={handleLogoClick}
                style={{ cursor: 'pointer' }}
              />
              
              <div 
                className="shop-name"
                onClick={handleShopNameClick}
                style={{ 
                  cursor: !isGuestUser ? 'pointer' : 'default',
                  opacity: !isGuestUser ? 1 : 0.8
                }}
              >
                {shopName}
              </div>
              
              {isGuestUser && (
                <div className="guest-badge">Guest</div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              
              <ThemeControls>
                <RefreshButton 
                  onClick={refreshTheme}
                  className={isRefreshing ? "spinning" : ""}
                  title="Get random theme"
                  theme={currentTheme}
                >
                  <RefreshCw size={16} />
                </RefreshButton>
                <PinButton 
                  onClick={togglePinStyle} 
                  isPinned={isPinned}
                  title={isPinned ? "Unpin this style" : "Pin this style"}
                  theme={currentTheme}
                >
                  <Pin size={16} fill={isPinned ? currentTheme?.colors?.accent : "none"} />
                </PinButton>
              </ThemeControls>
            </div>
          </HeaderContent>
        </PageHeader>
        
        <MainContent>
          <ContentWrapper>
            {/* ✅ UPDATED: Add touch handlers and ref for swipe detection */}
            <div 
              ref={chatListRef}
              onTouchStart={isMobile && !isGuestUser ? onTouchStart : undefined}
              onTouchMove={isMobile && !isGuestUser ? onTouchMove : undefined}
              onTouchEnd={isMobile && !isGuestUser ? onTouchEnd : undefined}
              style={{
                position: 'relative',
                width: isMobile ? '100%' : '350px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease-in-out',
                ...(isMobile && {
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  background: 'linear-gradient(to bottom, #0B0B3B, #1A1A4C)',
                  zIndex: 30,
                  transform: chatState.showChatList ? 'translateX(0)' : 'translateX(-100%)',
                  padding: '1rem'
                })
              }}
            >
              
              <SearchInput filterActive={chatState.activeFilter !== 'all'}>
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={chatState.searchTerm}
                  onChange={(e) => chatDispatch({ type: 'SET_SEARCH_TERM', payload: e.target.value })}
                />
                {chatState.searchTerm && (
                  <button 
                    className="clear-button"
                    onClick={() => chatDispatch({ type: 'SET_SEARCH_TERM', payload: '' })}
                  >
                    <X size={16} />
                  </button>
                )}
                
                {/* ✅ NEW: Filter button inside search bar (only for authenticated users) */}
                {!isGuestUser && (
                  <button
                    className="filter-button-inline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFilterDropdown(!showFilterDropdown);
                    }}
                  >
                    <Filter size={16} />
                  </button>
                )}
                
                {/* Filter dropdown attached to search bar */}
                {!isGuestUser && (
                  <FilterDropdown show={showFilterDropdown}>
                    <FilterOption
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '0.25rem', color: '#60A5FA' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const allIds = new Set(getFilteredChats.map(c => c.id));
                        setSelectedIds(allIds);
                        setSelectMode(true);
                        setShowFilterDropdown(false);
                      }}
                    >
                      <CheckSquare size={14} />
                      Select All
                      {getFilteredChats.length > 0 && <span className="count">{getFilteredChats.length}</span>}
                    </FilterOption>
                    {STATUS_FILTER_OPTIONS.map(option => {
                      const Icon = option.icon;
                      const count = getFilterCounts[option.key];
                      return (
                        <FilterOption
                          key={option.key}
                          active={chatState.activeFilter === option.key}
                          onClick={(e) => {
                            e.stopPropagation();
                            chatDispatch({ type: 'SET_FILTER', payload: option.key });
                            setShowFilterDropdown(false);
                          }}
                        >
                          <Icon />
                          {option.label}
                          {count > 0 && <span className="count">{count}</span>}
                        </FilterOption>
                      );
                    })}
                  </FilterDropdown>
                )}
              </SearchInput>

              {selectMode && (
                <SelectionBar>
                  <span className="count-label">{selectedIds.size} selected</span>
                  <button className="delete-sel" onClick={handleDeleteSelected} disabled={selectedIds.size === 0}>
                    <Trash2 size={13} /> Delete
                  </button>
                  <button className="cancel-sel" onClick={exitSelectMode}>Cancel</button>
                </SelectionBar>
              )}

              {/* ✅ HIDE tabs for guests - they can only see buying */}
{!chatState.isGuest && !isGuestUser && auth.currentUser && !auth.currentUser.isAnonymous && (
  <ModeTabs>
    {MODE_OPTIONS.map(option => (
      <ModeTab
        key={option.key}
        active={chatState.activeMode === option.key}
        onClick={() => chatDispatch({ type: 'SET_MODE', payload: option.key })}
      >
        {option.label}
      </ModeTab>
    ))}
  </ModeTabs>
)}

{/* Store selector — shown in selling mode when user has access to multiple shops */}
{!chatState.isGuest && !isGuestUser && auth.currentUser && !auth.currentUser.isAnonymous &&
 chatState.activeMode === 'selling' && accessibleShops.length > 1 && (
  <div style={{ position: 'relative', marginBottom: '0.25rem' }}>
    {/* Trigger button */}
    <button
      onClick={() => setShopSelectorOpen(o => !o)}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.4rem 0.85rem',
        background: 'rgba(128,0,0,0.12)',
        border: '1px solid rgba(128,0,0,0.3)',
        borderRadius: shopSelectorOpen ? '8px 8px 0 0' : '8px',
        fontSize: '0.78rem',
        color: 'rgba(255,255,255,0.75)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <Store size={13} style={{ flexShrink: 0, opacity: 0.8 }} />
      <span style={{ flex: 1 }}>
        Orders for&nbsp;
        <strong style={{ color: '#fff' }}>
          {accessibleShops.find(s => s.shopId === activeShopId)?.shopName || 'My Shop'}
        </strong>
      </span>
      <ChevronDown
        size={13}
        style={{ flexShrink: 0, opacity: 0.7, transform: shopSelectorOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
      />
    </button>

    {/* Dropdown */}
    {shopSelectorOpen && (
      <div style={{
        position: 'absolute', top: '100%', left: 0, right: 0,
        background: '#1a1a2e',
        border: '1px solid rgba(128,0,0,0.3)',
        borderTop: 'none',
        borderRadius: '0 0 8px 8px',
        zIndex: 50,
        overflow: 'hidden',
      }}>
        {accessibleShops.map(shop => {
          const isSelected = shop.shopId === activeShopId;
          return (
            <button
              key={shop.shopId}
              onClick={() => { setActiveShop(shop.shopId); setShopSelectorOpen(false); }}
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.45rem 0.85rem',
                background: isSelected ? 'rgba(128,0,0,0.25)' : 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                fontSize: '0.78rem',
                color: isSelected ? '#fff' : 'rgba(255,255,255,0.65)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Store size={12} style={{ flexShrink: 0, opacity: 0.7 }} />
              <span style={{ flex: 1 }}>{shop.shopName || 'My Shop'}</span>
              {shop.isOwn && (
                <span style={{ fontSize: '0.65rem', opacity: 0.5 }}>owner</span>
              )}
              {isSelected && (
                <Check size={11} style={{ flexShrink: 0, color: 'rgba(128,0,0,0.9)' }} />
              )}
            </button>
          );
        })}
      </div>
    )}
  </div>
)}

{/* AUTO-ACCEPT TOGGLE — selling mode only, authenticated users */}
{!chatState.isGuest && !isGuestUser && auth.currentUser && !auth.currentUser.isAnonymous && chatState.activeMode === 'selling' && (
  <AutoAcceptToggle
    enabled={autoAcceptEnabled}
    onClick={!autoAcceptLoading ? handleToggleAutoAccept : undefined}
    title={autoAcceptEnabled ? 'Click to disable auto-accept' : 'Click to enable auto-accept'}
    style={{ opacity: autoAcceptLoading ? 0.6 : 1, cursor: autoAcceptLoading ? 'not-allowed' : 'pointer' }}
  >
    <div className="toggle-icon">
      {autoAcceptEnabled ? <Zap size={16} /> : <ZapOff size={16} />}
    </div>
    <div className="toggle-label">
      <div className="title">
        {autoAcceptEnabled ? 'Auto-Accept On' : 'Auto-Accept Off'}
      </div>
      <div className="subtitle">
        {autoAcceptEnabled
          ? 'New orders accepted instantly'
          : 'You review each order manually'}
      </div>
    </div>
    <div className="toggle-switch" />
  </AutoAcceptToggle>
)}

{/* ✅ UPDATED: Show header for ALL guests (improved detection) */}
{(chatState.isGuest || isGuestUser || auth.currentUser?.isAnonymous) && (
  <div style={{
    padding: '0.75rem 1rem',
    background: 'rgba(255, 193, 7, 0.1)',
    border: '1px solid rgba(255, 193, 7, 0.3)',
    borderRadius: '8px',
    marginBottom: '1rem',
    textAlign: 'center'
  }}>
    <div style={{
      color: '#FFC107',
      fontWeight: 600,
      fontSize: '0.9rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    }}>
      <ShoppingCart size={16} />
      Guest Orders
    </div>
    <p style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>**Logging out will delete guest orders**</p>
  </div>
)}
              
              {/* ✅ UPDATED: Pass selectedChatId to MessagesList for highlighting */}
              <MessagesList
                chats={isGuestUser ?
                  chatState.chats.filter(chat => chat.isBuyer) :
                  getFilteredChats
                }
                selectedChat={chatState.selectedChat}
                selectedChatId={chatState.selectedChat?.id}
                loading={chatState.loading}
                searchTerm={chatState.searchTerm}
                onChatSelect={handleChatSelect}
                onDeleteChat={handleDeleteChat}
                selectMode={selectMode}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelectChat}
              />
            </div>
            
            <div style={{
              flex: 1,
              marginLeft: isMobile ? 0 : '2rem',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              background: 'rgba(0, 0, 0, 0.4)',
              borderRadius: isMobile ? 0 : '10px',
              border: isMobile ? 'none' : '1px solid rgba(128, 0, 0, 0.3)',
              overflow: 'hidden',
              boxShadow: isMobile ? 'none' : '0 5px 30px rgba(0, 0, 0, 0.2)',
              ...(isMobile && {
                position: displayChat ? 'relative' : 'absolute',
                left: displayChat ? '0' : '100%',
                width: '100%',
                transition: 'left 0.3s ease-in-out'
              })
            }}>
              {displayChat ? (
                <ChatView
                  chat={displayChat}
                  isMobile={isMobile}
                  onBackToList={handleBackToChatList}
                  activeShopId={chatState.activeMode === 'selling' ? activeShopId : null}
                  activeShopName={chatState.activeMode === 'selling' && !isOwnShop
                    ? (accessibleShops.find(s => s.shopId === activeShopId)?.shopName || null)
                    : null}
                />
              ) : (
                <EmptyState
                  isMobile={isMobile}
                  hasSelectedChat={!!chatState.selectedChat}
                />
              )}
            </div>
          </ContentWrapper>
        </MainContent>
      </PageContainer>
    </ThemeProvider>
  );
};

export default MessagesPage;